// src/lib/portfolioCache.ts
// Ladezeit Objektseite (18.10.2026). Befund: usePortfolio() lädt in JEDER Hook-Instanz beim
// Mount die komplette Liste ("list", alle Objekte mit vollem Snapshot) – auf /deal/:id
// gleichzeitig in AppShell (Seitenleiste) und DealDetail, dazu ein eigenes "get". Nach jeder
// Änderung lud zudem jede Instanz die Liste erneut. Drei bis vier parallele Aufrufe derselben
// Vercel-Funktion (bei Kaltstart je eigene Instanz) vor dem ersten Bild.
// Jetzt: eine gemeinsame Liste pro Nutzer (laufende Anfrage wird geteilt, 30 s frisch), und
// "get" bedient sich aus der Liste, wenn das Objekt schon geladen ist (typischer Weg: Klick
// aus „Meine Objekte“ oder der Seitenleiste). list und get liefern dieselben Zeilen
// (api/portfolio.ts: beide select("*"), beide nur eigene Objekte).

export const LISTE_FRISCH_MS = 30_000;
/** Bis zu diesem Alter darf "get" aus der Liste bedient werden (Änderungen in dieser Sitzung
 *  verwerfen die Liste ohnehin sofort; das Limit deckt nur Änderungen auf anderen Geräten ab). */
export const OBJEKT_AUS_LISTE_MS = 5 * 60_000;

interface Eintrag<T> {
  userId: string;
  zeit: number;
  promise: Promise<T[]>;
  daten: T[] | null;
}

let eintrag: Eintrag<unknown> | null = null;

/** Gemeinsame Liste: teilt eine laufende Anfrage und nutzt ein frisches Ergebnis wieder. */
export function geteilteListe<T>(userId: string, laden: () => Promise<T[]>, opts: { erzwingen?: boolean; jetzt?: number } = {}): Promise<T[]> {
  const jetzt = opts.jetzt ?? Date.now();
  const e = eintrag as Eintrag<T> | null;
  if (!opts.erzwingen && e && e.userId === userId && (e.daten === null || jetzt - e.zeit < LISTE_FRISCH_MS)) {
    return e.promise;
  }
  const neu: Eintrag<T> = { userId, zeit: jetzt, daten: null, promise: Promise.resolve([]) };
  neu.promise = laden().then(
    (d) => {
      neu.daten = d;
      return d;
    },
    (err) => {
      if (eintrag === (neu as Eintrag<unknown>)) eintrag = null; // Fehler nicht zwischenspeichern
      throw err;
    }
  );
  eintrag = neu as Eintrag<unknown>;
  return neu.promise;
}

/** Objekt aus der bereits geladenen Liste (oder null). */
export function objektAusListe<T extends { id: string }>(userId: string, id: string, jetzt: number = Date.now()): T | null {
  const e = eintrag as Eintrag<T> | null;
  if (!e || e.userId !== userId || !e.daten || jetzt - e.zeit >= OBJEKT_AUS_LISTE_MS) return null;
  return e.daten.find((o) => o.id === id) ?? null;
}

/** Nach jeder Änderung: nächste Abfrage lädt neu. */
export function listeVerwerfen(): void {
  eintrag = null;
}
